import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { apiService } from '../services/api';
import { Notification } from '../types';
import CustomerNavbar from '../components/CustomerNavbar';
import Footer from '../components/Footer';
import AnimatedLink from '../components/AnimatedLink';
import SeoHead from '../components/SeoHead';
import { HOME_SEO, ORGANIZATION_JSON_LD, softwareApplicationJsonLd } from '../utils/publicSite';
import { FeaturesIcons } from './features/FeaturesIcons';
import FeatureVideoTheater, { FeatureVideoTheaterHandle } from './features/FeatureVideoTheater';
import { HOME_VIDEOS } from './features/featureVideos';
import './HomePage.css';

interface BusinessCategory {
  id: string;
  name: string;
  description?: string;
}

type CategoryFilter = 'all' | string;

const EXTERNAL_BLOG_URL = process.env.REACT_APP_BLOG_URL?.trim() || '';
const KICKSTARTER_URL =
  process.env.REACT_APP_KICKSTARTER_URL?.trim() ||
  'https://www.kickstarter.com/projects/aacorebooking/aacore-booking-from-side-hustle-to-business?utm_source=aacore_booking&utm_medium=website&utm_campaign=kickstarter_prelaunch&utm_content=site_banner';
const BUSINESS_CHALLENGE_URL =
  process.env.REACT_APP_BUSINESS_CHALLENGE_URL?.trim() || '/business-challenge';
const LIVE_DEMO_PATH = '/features#demos';
const AI_BOOKING_VIDEO_INDEX = 1;

const SpotlightNavCard: React.FC<{
  to: string;
  labelledBy: string;
  children: React.ReactNode;
}> = ({ to, labelledBy, children }) => {
  const body = <div className="home-spotlight-card-body">{children}</div>;
  if (to.startsWith('http')) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        className="home-spotlight-card"
        aria-labelledby={labelledBy}
      >
        {body}
      </a>
    );
  }
  return (
    <Link to={to} className="home-spotlight-card" aria-labelledby={labelledBy}>
      {body}
    </Link>
  );
};

const HomePage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<CategoryFilter>('all');
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [businessCategories, setBusinessCategories] = useState<BusinessCategory[]>([]);
  const [unreadNotifications, setUnreadNotifications] = useState<Notification[]>([]);
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const desktopSearchInputRef = useRef<HTMLInputElement>(null);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);
  const videoTheaterRef = useRef<FeatureVideoTheaterHandle>(null);

  const focusVisibleSearchInput = () => {
    const input = desktopSearchInputRef.current;
    if (!input || input.disabled) return;
    try {
      input.focus({ preventScroll: true });
    } catch {
      input.focus();
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categories = await apiService.getBusinessCategories();
        const sortedCategories = [...categories].sort((a, b) => {
          if (a.name === 'Others') return 1;
          if (b.name === 'Others') return -1;
          return a.name.localeCompare(b.name);
        });
        setBusinessCategories(sortedCategories);
      } catch (error) {
        console.error('Error loading business categories:', error);
        setBusinessCategories([]);
      }
    };
    loadCategories();
  }, []);

  useEffect(() => {
    const loadNotifications = async () => {
      if (!user?.id) {
        setUnreadNotifications([]);
        return;
      }
      try {
        const response = await apiService.getNotifications();
        const notifications = response.data || [];
        const unread = notifications.filter((n: Notification) => !n.is_read);
        setUnreadNotifications(unread);
      } catch (error) {
        console.error('Error loading notifications:', error);
        setUnreadNotifications([]);
      }
    };
    loadNotifications();
  }, [user?.id]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target as Node)) {
        setIsCategoryDropdownOpen(false);
      }
    };
    if (isCategoryDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCategoryDropdownOpen]);

  useEffect(() => {
    const frameId = requestAnimationFrame(() => {
      const timer = setTimeout(() => {
        focusVisibleSearchInput();
      }, 0);
      return () => clearTimeout(timer);
    });
    return () => cancelAnimationFrame(frameId);
  }, []);

  const getCategoryLabel = (category: CategoryFilter): string => {
    if (category === 'all') return 'All Categories';
    const foundCategory = businessCategories.find((cat) => cat.id === category);
    return foundCategory ? foundCategory.name : 'All Categories';
  };

  const handleCategorySelect = (category: CategoryFilter) => {
    setSelectedCategory(category);
    setIsCategoryDropdownOpen(false);
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchTerm.trim()) params.set('q', searchTerm.trim());
    if (selectedCategory !== 'all') params.set('category', selectedCategory);
    const queryString = params.toString();
    navigate(`/search${queryString ? `?${queryString}` : ''}`);
  };

  return (
    <div className="aacore-home min-h-screen flex flex-col">
      <FeaturesIcons />
      <SeoHead
        title={HOME_SEO.title}
        description={HOME_SEO.description}
        canonicalPath={HOME_SEO.path}
        jsonLd={[ORGANIZATION_JSON_LD, softwareApplicationJsonLd()]}
      />
      <CustomerNavbar
        onLogout={handleLogout}
        showDashboardLink={true}
        showLogoutButton={true}
        showAuthButtons={true}
        unreadNotificationsCount={unreadNotifications.length}
        onNotificationsClick={() => navigate('/dashboard/customer/notifications')}
      />

      <main className="aacore-home-main flex-1">
        <div className="aacore-home-wrap w-full">
          <section className="home-top-row" aria-label="Announcement and learn">
            <div className="home-announce" aria-labelledby="home-kickstarter-heading">
              <div className="home-announce-copy">
                <p className="home-announce-label">
                  <i className="bi bi-bell" aria-hidden="true"></i>
                  Announcement
                </p>
                <h2 id="home-kickstarter-heading">Join Our Kickstarter Campaign</h2>
                <p>Help shape the next chapter of AACore Booking for small service businesses.</p>
              </div>
              <a
                href={KICKSTARTER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="home-announce-cta"
              >
                View campaign
                <i className="bi bi-arrow-right" aria-hidden="true"></i>
              </a>
            </div>

            <AnimatedLink to="/features/" className="home-learn-aside">
              <i className="bi bi-book home-learn-book" aria-hidden="true" />
              <strong>Learn About AACore Booking</strong>
              <i className="bi bi-arrow-right home-learn-arrow" aria-hidden="true" />
            </AnimatedLink>
          </section>

          <section className="home-discovery" aria-labelledby="search-title">
            <div className="home-hero">
              <div className="home-hero-copy text-center px-2">
                <div className="home-hero-mark mx-auto mb-3 flex items-center justify-center">
                  <i className="bi bi-search text-lg" aria-hidden="true"></i>
                </div>
                <h1 id="search-title" className="mb-2">Find Your Perfect Service</h1>
                <p className="mb-4">
                  Discover local businesses and book appointments with ease.
                  <br />
                  Now with AI to help you book and manage your appointments.
                </p>
              </div>

              <form
                className="home-search-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  handleSearch();
                }}
                role="search"
              >
                <div className="search-pill-group">
                    <div className="dropdown relative" ref={categoryDropdownRef}>
                      <button
                        type="button"
                        className="category-dropdown-btn dropdown-toggle"
                        onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                      >
                        <span className="flex items-center gap-2">
                          <i className="bi bi-grid-fill" aria-hidden="true"></i>
                          <span>{getCategoryLabel(selectedCategory)}</span>
                        </span>
                        <i className={`bi bi-chevron-down transition-transform ${isCategoryDropdownOpen ? 'rotate-180' : ''}`}></i>
                      </button>
                      {isCategoryDropdownOpen && (
                        <ul className="dropdown-menu home-category-menu">
                          <li className="home-category-heading">Popular categories</li>
                          <li>
                            <button type="button" onClick={() => handleCategorySelect('all')} className={selectedCategory === 'all' ? 'is-active' : ''}>
                              <i className="bi bi-grid-fill"></i> All categories
                            </button>
                          </li>
                          <li><hr /></li>
                          {businessCategories.map((category, index) => (
                            <React.Fragment key={category.id}>
                              {category.name === 'Others' && index > 0 && <li><hr /></li>}
                              <li>
                                <button
                                  type="button"
                                  onClick={() => handleCategorySelect(category.id)}
                                  className={selectedCategory === category.id ? 'is-active' : ''}
                                >
                                  <i className="bi bi-tag"></i> {category.name}
                                </button>
                              </li>
                            </React.Fragment>
                          ))}
                        </ul>
                      )}
                    </div>
                    <input
                      ref={desktopSearchInputRef}
                      type="search"
                      className="form-control"
                      placeholder="Search service, business, city, or phone"
                      aria-label="Search service, business, city, or phone"
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          handleSearch();
                        }
                      }}
                    />
                    <button className="aacore-btn-primary justify-center" type="submit">
                      <i className="bi bi-search" aria-hidden="true"></i><span>Search</span>
                    </button>
                  </div>
              </form>

              <ul className="home-trust">
                <li><i className="bi bi-shield-check" aria-hidden="true"></i>Secure</li>
                <li><i className="bi bi-bell" aria-hidden="true"></i>Reminders</li>
                <li><i className="bi bi-chat-left-heart" aria-hidden="true"></i>Reviews</li>
              </ul>
            </div>

            <div className="home-experience">
              <div className="home-ai" aria-labelledby="ai-title">
                <div className="home-ai-copy">
                  <p className="home-ai-intro">
                    <i className="bi bi-stars" aria-hidden="true"></i>
                    Meet Ask AACore <small>AI</small>
                  </p>
                  <h2 id="ai-title">Book through a conversation.</h2>
                  <p>
                    On a business&apos;s booking page, tell the AI assistant what you need. It prepares the booking, then asks you to review and confirm.
                  </p>
                  <ul className="home-ai-capabilities">
                    <li><i className="bi bi-check2" aria-hidden="true"></i>Book with chat</li>
                    <li><i className="bi bi-check2" aria-hidden="true"></i>Manage appointments</li>
                    <li><i className="bi bi-check2" aria-hidden="true"></i>You confirm</li>
                  </ul>
                  <div className="home-ai-actions">
                    <button
                      type="button"
                      className="home-ai-button"
                      onClick={() => videoTheaterRef.current?.play(AI_BOOKING_VIDEO_INDEX)}
                    >
                      <i className="bi bi-play-fill" aria-hidden="true"></i>
                      AI booking video demo
                    </button>
                    <Link className="home-ai-link" to={LIVE_DEMO_PATH}>Try the live demo</Link>
                  </div>
                  <p className="home-ai-voice">
                    <i className="bi bi-mic" aria-hidden="true"></i>
                    Voice booking and management are coming soon.
                  </p>
                </div>
                <FeatureVideoTheater ref={videoTheaterRef} videos={HOME_VIDEOS} />
              </div>
            </div>
          </section>

          <section className="home-spotlight" aria-label="Business Challenge and Resource Center">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <SpotlightNavCard to={BUSINESS_CHALLENGE_URL} labelledBy="home-business-challenge-heading">
                <div className="flex items-center gap-2 mb-3">
                  <div className="home-spotlight-icon" style={{ backgroundColor: '#ede9fe', color: '#6d28d9' }} aria-hidden="true">
                    <i className="bi bi-trophy text-base"></i>
                  </div>
                  <p className="home-spotlight-label" style={{ color: '#6d28d9' }}>Business Challenge</p>
                </div>
                <h2 id="home-business-challenge-heading">Build your business and enter to win part of $3,000.</h2>
                <p className="home-spotlight-copy">
                  New freelancers and existing service businesses can participate. Create or move your business to AACore, then submit your progress by November 15, 2026.
                </p>
                <p className="home-spotlight-fine">Four prizes. Winners announced Thanksgiving Day. Eligibility and conditions apply.</p>
              </SpotlightNavCard>

              <SpotlightNavCard to={EXTERNAL_BLOG_URL || '/blog/'} labelledBy="home-blog-resource-heading">
                <div className="flex items-center gap-2 mb-3">
                  <div className="home-spotlight-icon" style={{ backgroundColor: '#dbeafe', color: '#0B4778' }} aria-hidden="true">
                    <i className="bi bi-journal-text text-base"></i>
                  </div>
                  <p className="home-spotlight-label" style={{ color: '#1a6ea5' }}>Resource Center</p>
                </div>
                <h2 id="home-blog-resource-heading">Helpful articles for setup, booking, and platform updates</h2>
                <p className="home-spotlight-copy">
                  Business setup instructions, booking tips, release notes, compliance notes, and founder updates are organized in one place.
                </p>
                <div className="home-spotlight-tags">
                  {['Setup guides', 'Booking tips', 'Release notes', 'Compliance notes'].map((label) => (
                    <span key={label} className="home-spotlight-tag">{label}</span>
                  ))}
                </div>
              </SpotlightNavCard>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default HomePage;
